import React from "react";
import {
  Sofa,
  Truck,
  ShieldCheck,
  IndianRupee,
  CheckCircle2,
  ArrowRight,
  Star,
  Users,
  Clock3,
  Home,
} from "lucide-react";

const FurnitureRental = () => {
  const benefits = [
    "Plans starting @ ₹299/month",
    "Zero Security Deposit",
    "Free Delivery Across Bangalore",
    "Free Installation & Setup",
    "Premium Quality Furniture",
    "Flexible Rental Duration",
    "Easy Upgrade & Return",
    "Trusted by 1000+ Customers",
  ];

  const categories = [
    "Sofas",
    "Beds",
    "Mattresses",
    "Wardrobes",
    "Dining Sets",
    "Study Tables",
    "Office Furniture",
    "More",
  ];

  const stats = [
    {
      icon: <Users className="w-6 h-6" />,
      value: "1000+",
      label: "Happy Customers",
    },
    {
      icon: <Truck className="w-6 h-6" />,
      value: "Fast",
      label: "Delivery & Setup",
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      value: "100%",
      label: "Hassle Free",
    },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] via-white to-[#EEF2FF] py-24 px-4 md:px-10">
      
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-[30rem] h-[30rem] bg-green-100 rounded-full blur-3xl opacity-40"></div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-20">
          
          <div className="inline-flex items-center gap-2 bg-white border border-red-100 shadow-md px-5 py-2 rounded-full mb-6">
            <Sofa className="w-4 h-4 text-red-500" />
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">
              Furniture Rental Bangalore
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-gray-900 leading-tight">
            Premium Furniture
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#E63946] via-[#ff4d5a] to-[#ff7b7b]">
              On Rent
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Affordable Furniture. Flexible Plans. Zero Hassle.
            Rent premium furniture for your home or office with
            easy monthly plans starting at just ₹299/month.
          </p>
        </div>

        {/* Main Section */}
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          
          {/* LEFT CONTENT */}
          <div>
            
            <div className="inline-flex items-center gap-3 bg-green-50 border border-green-100 px-5 py-3 rounded-2xl mb-8">
              <IndianRupee className="w-5 h-5 text-green-600" />
              <span className="font-bold text-green-700">
                Starting from ₹299 / Month
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              Live Smart.
              <span className="block text-[#E63946]">
                Pay Monthly.
              </span>
            </h2>

            <p className="text-lg text-gray-600 mt-6 leading-relaxed">
              Furnish your dream home without spending lakhs upfront.
              Get premium furniture delivered, installed, and maintained
              by Housyhunt with flexible rental plans.
            </p>

            {/* Benefits */}
            <div className="grid sm:grid-cols-2 gap-4 mt-10">
              {benefits.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-xl transition-all duration-300"
                >
                  <div className="bg-red-50 p-2 rounded-xl text-red-500">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>

                  <span className="font-medium text-gray-700">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-wrap gap-4 mt-10">
              
              <button className="group bg-gradient-to-r from-[#E63946] to-[#ff5f6d] text-white px-8 py-4 rounded-2xl font-bold shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-3">
                Get Free Consultation
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <button className="bg-white border border-gray-200 hover:border-gray-300 px-8 py-4 rounded-2xl font-bold text-gray-800 shadow-lg transition-all duration-300">
                Browse Furniture Plans
              </button>
            </div>
          </div>

          {/* RIGHT IMAGE */}
          <div className="relative">
            
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.12)] border border-white">
              
              <img
                src="https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?q=80&w=1600&auto=format&fit=crop"
                alt="Furniture Rental"
                className="w-full h-[700px] object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

              {/* Badge */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-xl rounded-2xl px-5 py-3 shadow-xl">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">
                  Bangalore Exclusive
                </p>

                <h4 className="font-semibold text-gray-900 mt-1">
                  Zero Deposit Rental
                </h4>
              </div>

              {/* Bottom Content */}
              <div className="absolute bottom-8 left-8 right-8">
                
                <h3 className="text-4xl font-black text-white">
                  Furniture For Every Home
                </h3>

                <p className="text-gray-200 text-lg mt-4 leading-relaxed">
                  Sofas, Beds, Dining Sets, Study Tables, Office Furniture
                  and much more with affordable monthly rentals.
                </p>
              </div>
            </div>

            {/* Floating Card */}
            <div className="hidden md:block absolute -bottom-8 -left-8 bg-white/90 backdrop-blur-2xl rounded-3xl p-6 shadow-2xl w-72 border border-white">
              <div className="flex items-center gap-4">
                <div className="bg-green-100 text-green-600 p-4 rounded-2xl">
                  <Truck className="w-6 h-6" />
                </div>

                <div>
                  <h4 className="font-bold text-gray-900">
                    Free Delivery & Setup
                  </h4>

                  <p className="text-gray-500 text-sm mt-1">
                    Across Bangalore with expert installation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Furniture Categories */}
        <div className="mt-24">
          
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black text-gray-900">
              Everything You Need For Your Home
            </h2>

            <p className="text-gray-600 text-lg mt-4">
              Premium furniture for every room and every lifestyle.
            </p>
          </div>

          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {categories.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-3xl p-8 text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              >
                <Home className="w-10 h-10 text-red-500 mx-auto mb-4" />

                <h3 className="font-bold text-xl text-gray-900">
                  {item}
                </h3>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-24 bg-white/80 backdrop-blur-2xl border border-white rounded-[2.5rem] p-10 shadow-xl">
          
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((item, index) => (
              <div
                key={index}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>

                <h3 className="text-5xl font-black text-gray-900">
                  {item.value}
                </h3>

                <p className="text-gray-600 mt-2 font-medium">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="mt-24 text-center">
          
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 px-5 py-2 rounded-full mb-6">
            <Star className="w-4 h-4 text-red-500" />
            <span className="font-semibold text-red-600">
              Bangalore Only
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl font-black text-gray-900">
            Move In Today Without
            <span className="block text-[#E63946]">
              Buying Expensive Furniture
            </span>
          </h2>

          <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">
            Live Smart. Pay Monthly. No Deposit.
            Get premium furniture delivered to your doorstep.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <button className="bg-[#E63946] hover:bg-[#d42f3c] text-white px-8 py-4 rounded-2xl font-bold shadow-xl">
              Get Free Consultation
            </button>

            <button className="bg-white border border-gray-200 px-8 py-4 rounded-2xl font-bold text-gray-900 shadow-lg">
              Browse Furniture Plans
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FurnitureRental;